import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Check, Circle, Clock, Lightbulb, LoaderCircle, PartyPopper, Play, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EXAM_BY_ID } from "../data/exams";
import { LAB_BY_ID, LABS, LAB_SOURCES } from "../data/labs";
import type { BuildLab, ClassifyLab, Lab, OrderLab, SpotLab } from "../data/labTypes";
import { Inline } from "../components/RichText";
import { ScoreRing, useDocumentTitle } from "../components/ui";
import { gradeBuild, gradeClassify, gradeOrder, gradeSpot, type LabResult } from "../lib/labGrading";
import { recordLab } from "../lib/progress";
import { hashString, mulberry32, shuffle } from "../lib/random";
import { progressStore, useProgress } from "../lib/store";
import { LAB_TYPE_LABEL } from "./LabsPage";
import { localizeLab } from "../data/localized";
import { useT } from "../i18n";
import { NotFoundPage } from "./NotFoundPage";
import { api, ApiError, type SandboxRunResult } from "../lib/api";

function SandboxPanel() {
  const t = useT();
  const [language, setLanguage] = useState<"python" | "javascript">("python");
  const [code, setCode] = useState("print(\"Hello from the isolated lab!\")");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SandboxRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.labRunnerStatus().then((r) => { if (active) setAvailable(r.available); })
      .catch(() => { if (active) setAvailable(false); });
    return () => { active = false; };
  }, []);

  const chooseLanguage = (next: "python" | "javascript") => {
    setLanguage(next);
    setCode(next === "python" ? "print(\"Hello from the isolated lab!\")" : "console.log(\"Hello from the isolated lab!\");");
    setResult(null);
    setError(null);
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.runLabCode(language, code));
    } catch (e) {
      const code = e instanceof ApiError ? e.message : "";
      setError(code === "sandbox-not-configured"
        ? t("Sandbox chưa được cấu hình trên máy chủ.", "The sandbox is not configured on the server.")
        : code === "sandbox-busy" || (e instanceof ApiError && e.status === 429)
          ? t("Đang quá tải hoặc bạn đã chạy nhiều lần. Hãy thử lại sau.", "The runner is busy or you have reached the run limit. Try again shortly.")
          : t("Không chạy được code. Hãy thử lại sau.", "Could not run the code. Please try again."));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card mt">
      <h2>{t("Chạy code trong sandbox", "Run code in a sandbox")}</h2>
      <p className="small muted">{t("Mỗi lần chạy dùng một môi trường riêng, không có outbound internet, và tự hủy sau khi chạy. Không nhập thông tin bí mật.", "Each run uses a separate environment with no outbound internet and is destroyed afterward. Do not enter secrets.")}</p>
      <label className="label small" htmlFor="sandbox-language">{t("Ngôn ngữ", "Language")}</label>
      <select id="sandbox-language" value={language} disabled={running} onChange={(e) => chooseLanguage(e.target.value as "python" | "javascript")}>
        <option value="python">Python 3</option>
        <option value="javascript">JavaScript (Node.js)</option>
      </select>
      <label className="label small mt" htmlFor="sandbox-code">{t("Code (tối đa 12 KB)", "Code (up to 12 KB)")}</label>
      <textarea id="sandbox-code" className="prose" value={code} maxLength={12_000} spellCheck={false} disabled={running} onChange={(e) => setCode(e.target.value)} />
      <div className="row mt">
        <button className="btn primary" type="button" onClick={() => void run()} disabled={running || !code.trim() || available === false}>
          {running ? <><LoaderCircle className="ico" size={16} />{t("Đang chạy…", "Running…")}</> : <><Play className="ico" size={16} />{t("Chạy code", "Run code")}</>}
        </button>
        {available === false && <span className="small muted">{t("Runner chưa sẵn sàng trên máy chủ.", "The runner is not configured on the server.")}</span>}
      </div>
      {error && <p className="callout bad mt" role="alert">{error}</p>}
      {result && <div className="mt" aria-live="polite"><div className="small muted">{t(`Mã thoát: ${result.exitCode}`, `Exit code: ${result.exitCode}`)}{result.truncated ? ` · ${t("đã cắt bớt đầu ra", "output truncated")}` : ""}</div><pre><code>{result.output || t("(không có đầu ra)", "(no output)")}</code></pre></div>}
    </section>
  );
}

interface LabProps<T extends Lab> {
  lab: T;
  result: LabResult | null;
  onGrade: (r: LabResult) => void;
}

function initialOrder(lab: OrderLab): number[] {
  const idx = lab.steps.map((_, i) => i);
  let s = shuffle(idx, mulberry32(hashString(lab.id)));
  // Đảm bảo không trùng thứ tự đúng.
  if (s.every((v, i) => v === i)) s = [...s.slice(1), s[0] as number];
  return s;
}

function OrderExercise({ lab, result, onGrade }: LabProps<OrderLab>) {
  const t = useT();
  const [order, setOrder] = useState(() => initialOrder(lab));
  const move = (pos: number, dir: -1 | 1) => {
    const to = pos + dir;
    if (to < 0 || to >= order.length) return;
    const next = order.slice();
    [next[pos], next[to]] = [next[to] as number, next[pos] as number];
    setOrder(next);
  };
  return (
    <>
      <ol className="order-list">
        {order.map((stepIdx, pos) => {
          const state = result ? (result.detail[pos] ? " correct" : " incorrect") : "";
          return (
            <li key={stepIdx} className={`order-item${state}`}>
              <span className="num">{pos + 1}</span>
              <span className="text">{lab.steps[stepIdx]}</span>
              <button type="button" className="icon-btn" onClick={() => move(pos, -1)} disabled={pos === 0} aria-label={t(`Đưa bước "${lab.steps[stepIdx]}" lên`, `Move "${lab.steps[stepIdx]}" up`)}><ArrowUp size={18} /></button>
              <button type="button" className="icon-btn" onClick={() => move(pos, 1)} disabled={pos === order.length - 1} aria-label={t(`Đưa bước "${lab.steps[stepIdx]}" xuống`, `Move "${lab.steps[stepIdx]}" down`)}><ArrowDown size={18} /></button>
            </li>
          );
        })}
      </ol>
      <button type="button" className="btn primary mt" onClick={() => onGrade(gradeOrder(lab, order))}>{t("Chấm điểm", "Grade")}</button>
      {result && result.score < 100 && (
        <div className="callout mt">
          <strong>{t("Thứ tự đúng:", "Correct order:")}</strong>
          <ol className="mb-0">{lab.steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </div>
      )}
    </>
  );
}

function ClassifyExercise({ lab, result, onGrade }: LabProps<ClassifyLab>) {
  const t = useT();
  const [choices, setChoices] = useState<(number | undefined)[]>(() => lab.items.map(() => undefined));
  const complete = choices.every((c) => c !== undefined);
  return (
    <>
      <div className="stack" style={{ gap: "0.5rem" }}>
        {lab.items.map((it, i) => {
          const state = result ? (result.detail[i] ? " correct" : " incorrect") : "";
          return (
            <div key={it.text} className={`classify-item${state}`}>
              <div>
                <div>{it.text}</div>
                {result && (
                  <div className="small muted mt">
                    {result.detail[i] ? <><Check className="ico" size={16} aria-hidden="true" /> </> : <><X className="ico" size={16} aria-hidden="true" /> {t("Đúng là", "Correct")}: {lab.buckets[it.bucket]}. </>}
                    {it.why}
                  </div>
                )}
              </div>
              <select
                aria-label={`${t("Phân loại", "Classify")}: ${it.text}`}
                value={choices[i] ?? ""}
                onChange={(e) => {
                  const v = e.target.value === "" ? undefined : Number(e.target.value);
                  setChoices((c) => c.map((x, j) => (j === i ? v : x)));
                }}
              >
                <option value="">{t("— Chọn —", "— Choose —")}</option>
                {lab.buckets.map((b, bi) => <option key={b} value={bi}>{b}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      <button type="button" className="btn primary mt" disabled={!complete} onClick={() => onGrade(gradeClassify(lab, choices))}>
        {complete ? t("Chấm điểm", "Grade") : t("Phân loại hết các mục để chấm", "Classify every item to grade")}
      </button>
    </>
  );
}

function SpotExercise({ lab, result, onGrade }: LabProps<SpotLab>) {
  const t = useT();
  const [selected, setSelected] = useState<number[]>([]);
  const toggle = (i: number) => setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  return (
    <>
      <p className="small muted">{t("Bấm vào dòng để chọn/bỏ chọn", "Click a line to select/deselect")} ({lab.language}).</p>
      <div className="code-lines" role="group" aria-label={t("Các dòng code", "Code lines")}>
        {lab.lines.map((line, i) => {
          const isBug = lab.bugLines.includes(i);
          const sel = selected.includes(i);
          let cls = "code-line";
          if (result) {
            if (isBug) cls += " correct";
            else if (sel) cls += " incorrect";
          } else if (sel) cls += " selected";
          return (
            <button key={i} type="button" className={cls} aria-pressed={sel} onClick={() => !result && toggle(i)} disabled={!!result}>
              <span className="ln">{i + 1}</span>
              <span>{line}</span>
            </button>
          );
        })}
      </div>
      {!result && (
        <button type="button" className="btn primary mt" disabled={!selected.length} onClick={() => onGrade(gradeSpot(lab, selected))}>
          {t("Chấm điểm", "Grade")}
        </button>
      )}
      {result && (
        <div className="callout mt">
          <strong>{t("Cách sửa:", "Fix:")}</strong> <Inline text={lab.fix} />
        </div>
      )}
    </>
  );
}

function BuildExercise({ lab, result, onGrade }: LabProps<BuildLab>) {
  const t = useT();
  const [value, setValue] = useState(lab.starter);
  const [showSolution, setShowSolution] = useState(false);
  return (
    <>
      <label htmlFor="lab-editor" className="label small" style={{ fontWeight: 600 }}>
        {lab.format === "json" ? t("Soạn JSON", "Write JSON") : t("Viết prompt của bạn", "Write your prompt")}
      </label>
      <textarea
        id="lab-editor"
        className={lab.format === "text" ? "prose" : undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        spellCheck={lab.format === "text"}
        placeholder={lab.format === "text" ? t("Viết prompt hoàn chỉnh tại đây…", "Write your complete prompt here…") : undefined}
      />
      <div className="row mt">
        <button type="button" className="btn primary" onClick={() => onGrade(gradeBuild(lab, value))} disabled={!value.trim()}>{t("Chấm điểm", "Grade")}</button>
        <button type="button" className="btn ghost" onClick={() => setShowSolution((s) => !s)}>{showSolution ? t("Ẩn lời giải mẫu", "Hide sample solution") : t("Xem lời giải mẫu", "Show sample solution")}</button>
        {lab.starter && <button type="button" className="btn ghost" onClick={() => setValue(lab.starter)}>{t("Khôi phục ban đầu", "Reset")}</button>}
      </div>
      {result?.error && <div className="callout bad mt">{result.error}</div>}
      <h3 className="mt">{t("Tiêu chí chấm", "Grading criteria")}</h3>
      <ul className="checklist">
        {lab.checks.map((c, i) => {
          const state = result ? (result.detail[i] ? "pass" : "fail") : "";
          return (
            <li key={c.label} className={state}>
              <span className="mark" aria-hidden="true">{result ? (result.detail[i] ? <Check size={18} strokeWidth={3} /> : <X size={18} strokeWidth={3} />) : <Circle size={16} />}</span>
              <span>
                {c.label}
                {result && !result.detail[i] && <div className="small muted"><Lightbulb className="ico" size={14} aria-hidden="true" /> {c.hint}</div>}
                {result && <span className="sr-only">{result.detail[i] ? t(" — đạt", " — passed") : t(" — chưa đạt", " — not passed")}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="small muted mt">
        {lab.format === "text"
          ? t("Điểm tự động chỉ kiểm tra một số dấu hiệu của rubric; không hiểu hay xác nhận chất lượng/ngữ nghĩa của prompt. Hãy tự rà soát các tiêu chí và tình huống biên.", "The automatic score checks a few rubric signals; it does not understand or certify prompt quality or meaning. Review the criteria and edge cases yourself.")
          : t("Điểm tự động chỉ kiểm tra các tiêu chí hiển thị, không phải validator đầy đủ của JSON Schema hoặc xác nhận schema được API chấp nhận.", "The automatic score checks only the displayed criteria; it is not a full JSON Schema validator or confirmation that the API will accept the schema.")}
      </p>
      {showSolution && (
        <>
          <h3 className="mt">{t("Lời giải mẫu", "Sample solution")}</h3>
          <pre><code>{lab.solution}</code></pre>
          <p className="tiny muted">{t("Có nhiều lời giải đúng — đây chỉ là một ví dụ.", "Many answers are valid — this is just one example.")}</p>
        </>
      )}
    </>
  );
}

export function LabPage() {
  const { labId } = useParams();
  const p = useProgress();
  const t = useT();
  const raw = labId ? LAB_BY_ID.get(labId) : undefined;
  const lab = useMemo(() => (raw ? localizeLab(raw, p.lang) : undefined), [raw, p.lang]);
  const [result, setResult] = useState<LabResult | null>(null);
  const [attempt, setAttempt] = useState(0);
  useDocumentTitle(lab?.title ?? t("Không tìm thấy", "Not found"));
  const nextLab = useMemo(() => {
    const n = raw ? LABS[LABS.indexOf(raw) + 1] : undefined;
    return n ? localizeLab(n, p.lang) : undefined;
  }, [raw, p.lang]);

  if (!lab) return <NotFoundPage />;
  const best = p.labs[lab.id]?.best;

  const onGrade = (r: LabResult) => {
    setResult(r);
    if (!r.error) progressStore.update((x) => recordLab(x, lab.id, r.score, Date.now()));
  };
  const retry = () => {
    setResult(null);
    setAttempt((a) => a + 1);
  };
  const exerciseKey = `${lab.id}-${attempt}`;
  // Lab dạng "build" giữ nguyên nội dung đang soạn khi chấm lại.
  const resettable = lab.type !== "build";

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <p className="small"><Link to="/labs"><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Tất cả lab", "All labs")}</Link></p>
      <div className="row">
        <span className="chip">{t(LAB_TYPE_LABEL[lab.type].vi, LAB_TYPE_LABEL[lab.type].en)}</span>
        <span className="chip" style={{ color: EXAM_BY_ID[lab.exam].accent }}>{EXAM_BY_ID[lab.exam].code}</span>
        <span className="chip">{lab.skill}</span>
        <span className="small muted"><Clock className="ico" size={16} aria-hidden="true" /> {lab.minutes} {t("phút", "min")}</span>
        {best !== undefined && <span className={`chip ${best === 100 ? "ok" : "warn"}`}>{t("Tốt nhất", "Best")}: {best}/100</span>}
      </div>
      <h1 className="mt">{lab.title}</h1>
      <div className="card">
        <p><strong>{t("Tình huống:", "Scenario:")}</strong> <Inline text={lab.intro} /></p>
        <p className="mb-0"><strong>{t("Nhiệm vụ:", "Task:")}</strong> <Inline text={lab.task} /></p>
      </div>

      {!!LAB_SOURCES[lab.id]?.length && (
        <div className="card mt">
          <strong>{t("Nguồn chính thức", "Official references")}</strong>
          <ul className="mb-0">
            {LAB_SOURCES[lab.id]?.map((source) => (
              <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></li>
            ))}
          </ul>
        </div>
      )}

      <div className="card mt">
        {lab.type === "order" && <OrderExercise key={exerciseKey} lab={lab} result={result} onGrade={onGrade} />}
        {lab.type === "classify" && <ClassifyExercise key={exerciseKey} lab={lab} result={result} onGrade={onGrade} />}
        {lab.type === "spot" && <SpotExercise key={exerciseKey} lab={lab} result={result} onGrade={onGrade} />}
        {lab.type === "build" && <BuildExercise key={`${lab.id}-${p.lang}`} lab={lab} result={result} onGrade={onGrade} />}
      </div>

      <SandboxPanel />

      {result && !result.error && (
        <div className="card mt row" style={{ gap: "1.25rem" }} aria-live="polite">
          <ScoreRing percent={result.score} color={result.score === 100 ? "var(--ok)" : undefined}>
            <div><div className="big">{result.score}</div><div className="tiny muted">/ 100</div></div>
          </ScoreRing>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3>{lab.type === "build"
              ? t(`Đạt ${result.detail.filter(Boolean).length}/${result.detail.length} tiêu chí tự động`, `${result.detail.filter(Boolean).length}/${result.detail.length} automatic criteria passed`)
              : result.score === 100
                ? <><PartyPopper className="ico" size={20} aria-hidden="true" />{t("Hoàn hảo!", "Perfect!")}</>
                : result.score >= 60 ? t("Gần đạt rồi!", "Almost there!") : t("Thử lại nhé", "Try again")}</h3>
            <p className="small"><strong>{t("Bài học rút ra:", "Takeaway:")}</strong> {lab.takeaway}</p>
            <div className="row">
              {resettable && <button type="button" className="btn" onClick={retry}>{t("Làm lại", "Retry")}</button>}
              {nextLab && <Link className="btn primary" to={`/labs/${nextLab.id}`}>{t("Lab tiếp", "Next lab")}: {nextLab.title} <ArrowRight className="ico" size={16} aria-hidden="true" /></Link>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
